// src/modules/app-views/crm/pages/Boards/tabs/VoipTab/DialPad.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useRef, useEffect, useCallback, useState, useMemo } from 'react';
import translateX from 'utils/translateX';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const COLORS = {
  bodyBg: '#d1d8e0',
  bodyShadow: '#a5b1c2',
  screenBg: '#cceebb',
  screenText: '#2d3436',
  buttonBgTop: '#f5f6fa',
  buttonBgBottom: '#dcdde1',
  buttonPressed: '#bdc3c7',
  buttonText: '#2f3640',
  accentCall: '#2ecc71',
  accentCallDisabled: '#95a5a6',
  accentClear: '#e74c3c',
};

const LAYOUT = {
  canvasWidth: 260,
  canvasHeight: 420,
  padding: 16,
  displayY: 40,
  displayHeight: 65,
  buttonMargin: 10,
  rows: 5,
  cols: 3,
};

const KEYPAD_GRID = [
  ['1', '2', '3'],
  ['4', '5', '6'],
  ['7', '8', '9'],
  ['*', '0', '#'],
];

// DTMF tone frequencies (real telephone dual-tone pairs)
const DTMF_FREQS = {
  '1': [697, 1209], '2': [697, 1336], '3': [697, 1477],
  '4': [770, 1209], '5': [770, 1336], '6': [770, 1477],
  '7': [852, 1209], '8': [852, 1336], '9': [852, 1477],
  '*': [941, 1209], '0': [941, 1336], '#': [941, 1477],
};

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const DialPad = props => {
  // ---------------------------------------------------------------------------
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ---------------------------------------------------------------------------
  const { phoneNumber, onDigit, onBackspace, onCall, onClear, calling, active } = props;

  // ---------------------------------------------------------------------------
  // 2. HOOKS & REFS
  // ---------------------------------------------------------------------------
  const canvasRef = useRef(null);

  // ---------------------------------------------------------------------------
  // 4. LOCAL STATE
  // ---------------------------------------------------------------------------
  const [pressedBtnId, setPressedBtnId] = useState(null);

  // ---------------------------------------------------------------------------
  // 3. LOCAL VARIABLES I
  // ---------------------------------------------------------------------------
  const calculatedLayout = useMemo(() => {
    const keypadStartY = LAYOUT.displayY + LAYOUT.displayHeight + LAYOUT.buttonMargin;
    const usableHeight = LAYOUT.canvasHeight - keypadStartY - LAYOUT.padding;
    const usableWidth = LAYOUT.canvasWidth - LAYOUT.padding * 2;
    const buttonWidth =
      (usableWidth - LAYOUT.buttonMargin * (LAYOUT.cols - 1)) / LAYOUT.cols;
    const buttonHeight =
      (usableHeight - LAYOUT.buttonMargin * (LAYOUT.rows - 1)) / LAYOUT.rows;
    const actionRowY =
      keypadStartY + 4 * (buttonHeight + LAYOUT.buttonMargin);

    const buttons = [];

    // Numeric buttons
    for (let row = 0; row < 4; row++) {
      for (let col = 0; col < 3; col++) {
        const label = KEYPAD_GRID[row][col];
        const x = LAYOUT.padding + col * (buttonWidth + LAYOUT.buttonMargin);
        const y = keypadStartY + row * (buttonHeight + LAYOUT.buttonMargin);
        buttons.push({
          id: label,
          label,
          x,
          y,
          width: buttonWidth,
          height: buttonHeight,
          type: 'num',
        });
      }
    }

    // Clear button
    buttons.push({
      id: 'CLEAR',
      label: 'C',
      x: LAYOUT.padding,
      y: actionRowY,
      width: buttonWidth,
      height: buttonHeight,
      type: 'action',
      color: COLORS.accentClear,
    });

    // Call button (spans 2 columns)
    buttons.push({
      id: 'CALL',
      label: translateX('goto_call_button').toUpperCase(),
      x: LAYOUT.padding + buttonWidth + LAYOUT.buttonMargin,
      y: actionRowY,
      width: buttonWidth * 2 + LAYOUT.buttonMargin,
      height: buttonHeight,
      type: 'action',
      color: COLORS.accentCall,
    });

    return { buttons, buttonWidth, buttonHeight };
  }, []);

  // ---------------------------------------------------------------------------
  // 5. EFFECTS
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const { buttons } = calculatedLayout;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Body gradient
    const grd = ctx.createLinearGradient(0, 0, 0, canvas.height);
    grd.addColorStop(0, COLORS.bodyBg);
    grd.addColorStop(1, COLORS.bodyShadow);
    drawRoundedRect(
      ctx,
      4,
      4,
      canvas.width - 8,
      canvas.height - 8,
      24,
      grd,
      true,
    );

    // Speaker grills
    ctx.fillStyle = '#7f8c8d';
    const grillCount = 5;
    const grillWidth = 8;
    const grillSpacing = 14;
    const grillTotalWidth = grillCount * grillSpacing;
    const grillStartX = (canvas.width - grillTotalWidth) / 2;
    for (let i = 0; i < grillCount; i++) {
      drawRoundedRect(
        ctx,
        grillStartX + i * grillSpacing,
        20,
        grillWidth,
        5,
        2,
        '#7f8c8d',
      );
    }

    // Display frame
    drawRoundedRect(
      ctx,
      LAYOUT.padding,
      40,
      canvas.width - LAYOUT.padding * 2,
      LAYOUT.displayHeight,
      8,
      '#34495e',
    );

    // Display screen
    const screenMargin = 4;
    drawRoundedRect(
      ctx,
      LAYOUT.padding + screenMargin,
      40 + screenMargin,
      canvas.width - LAYOUT.padding * 2 - screenMargin * 2,
      LAYOUT.displayHeight - screenMargin * 2,
      4,
      COLORS.screenBg,
    );

    // Display text
    ctx.fillStyle = COLORS.screenText;
    ctx.font = 'bold 28px "Courier New", monospace';
    ctx.textAlign = 'right';
    ctx.textBaseline = 'middle';
    let textToShow = phoneNumber || '';
    if (textToShow.length > 11) textToShow = '...' + textToShow.slice(-10);
    ctx.fillText(
      textToShow,
      canvas.width - LAYOUT.padding - 12,
      40 + LAYOUT.displayHeight / 2,
    );

    // Buttons
    buttons.forEach(btn => {
      const isPressed = btn.id === pressedBtnId;
      const isCallBtn = btn.id === 'CALL';
      const isCallDisabled = isCallBtn && (calling || !phoneNumber);
      let bgStyle;

      if (isPressed && !isCallDisabled) {
        bgStyle = btn.color ? btn.color : COLORS.buttonPressed;
      } else {
        const grdBtn = ctx.createLinearGradient(
          btn.x,
          btn.y,
          btn.x,
          btn.y + btn.height,
        );
        if (isCallDisabled) {
          grdBtn.addColorStop(0, lightenDarkenColor(COLORS.accentCallDisabled, 20));
          grdBtn.addColorStop(1, COLORS.accentCallDisabled);
        } else if (btn.color) {
          grdBtn.addColorStop(0, lightenDarkenColor(btn.color, 20));
          grdBtn.addColorStop(1, btn.color);
        } else {
          grdBtn.addColorStop(0, COLORS.buttonBgTop);
          grdBtn.addColorStop(1, COLORS.buttonBgBottom);
        }
        bgStyle = grdBtn;
      }

      drawRoundedRect(
        ctx,
        btn.x,
        btn.y,
        btn.width,
        btn.height,
        10,
        bgStyle,
        !isPressed,
      );

      // Button text
      const hasColor = btn.color || isCallDisabled;
      ctx.fillStyle = hasColor && !isPressed ? 'white' : COLORS.buttonText;
      ctx.font =
        isCallBtn ? 'bold 15px sans-serif' : 'bold 20px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      let label = btn.label;
      if (isCallBtn && calling) {
        label = translateX('goto_call_calling').toUpperCase();
      }

      ctx.fillText(
        label,
        btn.x + btn.width / 2,
        btn.y + btn.height / 2 + (isPressed && !isCallDisabled ? 2 : 0),
      );
    });
  }, [phoneNumber, pressedBtnId, calculatedLayout, calling]);

  // Keyboard capture: digits + backspace when drawer voip tab is active
  const handleKeyDown = useCallback(e => {
    // Skip if focus is inside an input, textarea, or contenteditable
    const tag = document.activeElement?.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || document.activeElement?.isContentEditable) return;

    const key = e.key;

    if (/^[0-9*#]$/.test(key)) {
      e.preventDefault();
      playDtmf(key);
      onDigit(key);
      setPressedBtnId(key);
      setTimeout(() => setPressedBtnId(null), 150);
      return;
    }

    if (key === 'Backspace') {
      e.preventDefault();
      playBeep();
      onBackspace();
      setPressedBtnId('CLEAR');
      setTimeout(() => setPressedBtnId(null), 150);
      return;
    }

    if (key === 'Enter' && phoneNumber && !calling) {
      e.preventDefault();
      onCall();
      setPressedBtnId('CALL');
      setTimeout(() => setPressedBtnId(null), 150);
    }
  }, [onDigit, onBackspace, onCall, phoneNumber, calling]);

  useEffect(() => {
    if (!active) return;
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [active, handleKeyDown]);

  // ---------------------------------------------------------------------------
  // 6. HANDLERS
  // ---------------------------------------------------------------------------
  const handleMouseDown = e => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const clickX = (e.clientX - rect.left) * scaleX;
    const clickY = (e.clientY - rect.top) * scaleY;

    const clickedBtn = calculatedLayout.buttons.find(
      btn =>
        clickX >= btn.x &&
        clickX <= btn.x + btn.width &&
        clickY >= btn.y &&
        clickY <= btn.y + btn.height,
    );

    if (!clickedBtn) return;

    // Visual press feedback
    setPressedBtnId(clickedBtn.id);

    if (clickedBtn.type === 'num') {
      playDtmf(clickedBtn.label);
      onDigit(clickedBtn.label);
    } else if (clickedBtn.id === 'CLEAR') {
      playBeep();
      onBackspace();
    } else if (clickedBtn.id === 'CALL') {
      if (!calling && phoneNumber) {
        onCall();
      }
    }

    // Release visual feedback
    setTimeout(() => {
      if (canvasRef.current) {
        setPressedBtnId(null);
      }
    }, 150);
  };

  // ---------------------------------------------------------------------------
  // 8. UI
  // ---------------------------------------------------------------------------
  return (
    <div style={{ display: 'flex', justifyContent: 'center', marginTop: 48 }}>
      <canvas
        ref={canvasRef}
        width={LAYOUT.canvasWidth}
        height={LAYOUT.canvasHeight}
        onMouseDown={handleMouseDown}
        style={{
          display: 'block',
          cursor: 'pointer',
          userSelect: 'none',
          outline: 'none',
          border: 'none',
        }}
      />
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default DialPad;

// *******************************************************************************
// HELPERS
// *******************************************************************************

function lightenDarkenColor(col, amt) {
  let usePound = false;
  if (col[0] === '#') {
    col = col.slice(1);
    usePound = true;
  }
  const num = parseInt(col, 16);
  let r = (num >> 16) + amt;
  if (r > 255) r = 255;
  else if (r < 0) r = 0;
  let b = ((num >> 8) & 0x00ff) + amt;
  if (b > 255) b = 255;
  else if (b < 0) b = 0;
  let g = (num & 0x0000ff) + amt;
  if (g > 255) g = 255;
  else if (g < 0) g = 0;
  return (usePound ? '#' : '') + (g | (b << 8) | (r << 16)).toString(16);
}

function drawRoundedRect(ctx, x, y, width, height, radius, fillStyle, shadow = false) {
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + width - radius, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
  ctx.lineTo(x + width, y + height - radius);
  ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
  ctx.lineTo(x + radius, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();

  if (shadow) {
    ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
    ctx.shadowBlur = 6;
    ctx.shadowOffsetY = 3;
  } else {
    ctx.shadowColor = 'transparent';
  }

  ctx.fillStyle = fillStyle;
  ctx.fill();
  ctx.shadowColor = 'transparent';
}

// Shared AudioContext (lazy init to comply with browser autoplay policy)
let audioCtx = null;

function playDtmf(key) {
  const freqs = DTMF_FREQS[key];
  if (!freqs) return;

  if (!audioCtx) audioCtx = new (window.AudioContext || window.webkitAudioContext)();
  if (audioCtx.state === 'suspended') audioCtx.resume();

  const duration = 0.15;
  const now = audioCtx.currentTime;

  // Two sine oscillators (the real DTMF dual-tone)
  for (const freq of freqs) {
    const osc = audioCtx.createOscillator();
    const gain = audioCtx.createGain();
    osc.type = 'sine';
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(0.15, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + duration);
    osc.connect(gain);
    gain.connect(audioCtx.destination);
    osc.start(now);
    osc.stop(now + duration);
  }
}

function playBeep() {
  if (!audioCtx) audioCtx = new (window.AudioContext || window.webkitAudioContext)();
  if (audioCtx.state === 'suspended') audioCtx.resume();

  const duration = 0.08;
  const now = audioCtx.currentTime;
  const osc = audioCtx.createOscillator();
  const gain = audioCtx.createGain();
  osc.type = 'sine';
  osc.frequency.value = 480;
  gain.gain.setValueAtTime(0.1, now);
  gain.gain.exponentialRampToValueAtTime(0.001, now + duration);
  osc.connect(gain);
  gain.connect(audioCtx.destination);
  osc.start(now);
  osc.stop(now + duration);
}
